import * as Dialog from '@radix-ui/react-dialog';
import { X, AlertCircle, AlertTriangle, Info, CheckCircle, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { useError } from '../../context/ErrorContext';
import { useState } from 'react';

const ErrorModal = () => {
  const { error, clearError } = useError();
  const [showDetails, setShowDetails] = useState(false);

  if (!error) return null;

  const getIcon = () => {
    switch (error.type) {
      case 'success':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-6 w-6 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      case 'info':
        return <Info className="h-6 w-6 text-blue-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-red-600" />;
    }
  };

  const getColors = () => {
    switch (error.type) {
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-400',
          text: 'text-green-800',
          button: 'bg-green-600 hover:bg-green-700',
          detailsBg: 'bg-green-100'
        };
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-800',
          button: 'bg-red-600 hover:bg-red-700',
          detailsBg: 'bg-red-100'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-400',
          text: 'text-yellow-800',
          button: 'bg-yellow-600 hover:bg-yellow-700',
          detailsBg: 'bg-yellow-100'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-400',
          text: 'text-blue-800',
          button: 'bg-blue-600 hover:bg-blue-700',
          detailsBg: 'bg-blue-100'
        };
      default:
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-800',
          button: 'bg-red-600 hover:bg-red-700',
          detailsBg: 'bg-red-100'
        };
    }
  };

  const colors = getColors();

  const copyErrorDetails = () => {
    const details = `
Error: ${error.message}
Type: ${error.type}
Code: ${error.code || 'N/A'}
Time: ${error.timestamp?.toLocaleString() || 'N/A'}
Details: ${JSON.stringify(error.details, null, 2)}
    `.trim();
    navigator.clipboard.writeText(details);
    alert('Error details copied to clipboard!');
  };

  return (
    <Dialog.Root open={!!error} onOpenChange={clearError}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" />
        <Dialog.Content
          className={`fixed z-50 top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-md translate-x-[-50%] translate-y-[-50%] rounded-lg p-6 shadow-xl focus:outline-none data-[state=open]:animate-contentShow ${colors.bg} border ${colors.border}`}
          onEscapeKeyDown={clearError}
          onPointerDownOutside={clearError}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">{getIcon()}</div>
              <div className="ml-3">
                <Dialog.Title className={`text-lg leading-6 font-bold ${colors.text}`}>
                  {error.type === 'success' ? 'Success' : error.type.charAt(0).toUpperCase() + error.type.slice(1)}
                </Dialog.Title>
                <Dialog.Description className={`mt-2 text-sm ${colors.text}`}>
                  {error.message}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                className="rounded-full p-1 hover:bg-gray-200 transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </Dialog.Close>
          </div>
          {error.details && Object.keys(error.details).length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className={`flex items-center text-sm font-medium ${colors.text} hover:underline`}
              >
                {showDetails ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>
              
              {showDetails && (
                <pre className={`mt-2 p-3 rounded-md text-xs font-mono ${colors.detailsBg} overflow-auto max-h-[200px]`}>
                  {JSON.stringify(error.details, null, 2)}
                </pre>
              )}
            </div>
          )}
          <div className="mt-6 flex justify-between items-center gap-3">
            <button
              onClick={copyErrorDetails}
              className="flex items-center px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-200"
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy Details
            </button>
            <Dialog.Close asChild>
              <button
                className={`px-4 py-2 rounded-md text-white text-sm font-medium ${colors.button} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                Dismiss
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ErrorModal;